import { put, call, takeLatest, fork, select } from 'redux-saga/effects'
import moment from 'moment'
import { indicateProgress } from 'api'
import { countryFromHost } from 'shared/country'
import featureToggles from 'shared/featureToggles'
import { languageSelector } from 'i18n/selectors'
import { accountKeySelector, userAccountIdSelector } from 'auth/selectors'
import { getSurveyTexts } from './utils'

import { getActualSurvey } from './selectors'
import { getChartDataArray } from './utils'
import * as constants from './constants'
import * as actions from './actions'
import * as api from './api'

export function* getAllSurveysAdminSaga() {
    try {
        const currentCountry = countryFromHost()
        const surveys = yield call(
            indicateProgress,
            [constants.GET_ALL_SURVEY_BY_COUNTRY_ADMIN],
            api.getAllSurveysByCountryAdmin,
            currentCountry
        )
        const currentDate = moment(new Date()).format('YYYY-MM-DD')

        const actualSurvey = surveys.filter(survey => currentDate >= survey.dateStart && currentDate <= survey.dateEnd)

        yield put(actions.setAllSurveysByCountryAdmin(surveys))
        yield put(actions.setActualSurveyByCountryAdmin(...actualSurvey))

        return true
    } catch (e) {
        return false
    }
}

export function* npsAdminLoginSaga() {
    yield takeLatest(constants.NPS_ADMIN_LOGIN, function* ({ username, password }) {
        api.setAdminCredentials(username, password)

        // Since API doesn't have auth endpoint, we verify credits via existing endpoint.
        const result = yield call(getAllSurveysAdminSaga)

        if (result) {
            yield put(actions.setNpsAdminAuthError(null))
            yield put(actions.setNpsAdminAuthenticated(true))
        } else {
            api.clearAdminCredentials()
            yield put(actions.setNpsAdminAuthenticated(false))
            yield put(actions.setNpsAdminAuthError('nps.admin.login.error'))
        }
    })
}

export function* getSurveyResultsByIdSaga() {
    yield takeLatest(constants.GET_SURVEY_RESULTS_BY_ID, function* ({ id }) {
        const currentCountry = countryFromHost()
        const actualSurvey = yield select(getActualSurvey)
        const results = yield call(
            indicateProgress,
            [constants.GET_SURVEY_RESULTS_BY_ID],
            api.getSurveyResultsById,
            currentCountry,
            id || actualSurvey.surveyId
        )

        yield put(actions.setSurveyResultsById(results))
        yield put(actions.setChartData(getChartDataArray(results)))
    })
}

export function* getSurveyTextAnswerByIdSaga() {
    yield takeLatest(constants.GET_SURVEY_TEXT_ANSWER_BY_ID, function* ({ id, rating, page }) {
        const currentCountry = countryFromHost()
        const actualSurvey = yield select(getActualSurvey)
        const pageSize = 6

        const results = yield call(
            indicateProgress,
            ['GET_SURVEY_TEXT_ANSWER_BY_ID'],
            api.getSurveyTextAnswersById,
            currentCountry,
            id || actualSurvey.surveyId,
            page,
            pageSize,
            rating
        )

        yield put(actions.setSurveyTextAnswersById(results))
    })
}

export function* getAllUsersSurveysSaga() {
    yield takeLatest(constants.GET_ALL_SURVEYS_BY_COUNTRY, function* () {
        const currentCountry = countryFromHost()
        const accountKey = yield select(accountKeySelector)
        const language = yield select(languageSelector)
        const userId = yield select(userAccountIdSelector)

        const surveys = yield call(
            indicateProgress,
            [constants.GET_ALL_SURVEYS_BY_COUNTRY_PROCESS],
            api.getAllUsersSurveysByCountry,
            currentCountry,
            language,
            featureToggles.caPurchaseFlow ? accountKey : userId
        )

        yield put(actions.setAllSurveysByCountry(surveys))
    })
}

export function* sendSurveyAnswerSaga() {
    yield takeLatest(constants.SEND_SURVEY_ANSWER, function* ({ rating, ratingText }) {
        const currentCountry = countryFromHost()
        const accountKey = yield select(accountKeySelector)
        const language = yield select(languageSelector)
        const userId = yield select(userAccountIdSelector)

        try {
            yield indicateProgress([constants.SEND_SURVEY_ANSWER_PROCESS], function* () {
                yield call(
                    api.sendUsersSurveyByUseId,
                    currentCountry,
                    language,
                    featureToggles.caPurchaseFlow ? accountKey : userId,
                    rating,
                    ratingText
                )
            })
            yield put(actions.setAnswerSendingResult(true))
        } catch (error) {
            yield put(actions.setAnswerSendingResult(false))
        }
    })
}

export function* addNewSurveyByCountrySaga() {
    yield takeLatest(constants.ADD_NEW_SURVEY_BY_COUNTRY, function* ({ data }) {
        const currentCountry = countryFromHost()
        const validPrefixes = ['en', 'fr', 'pt']
        const surveyTexts = getSurveyTexts(data, validPrefixes)
        const newSurvey = {
            name: data.surveyName,
            dateStart: data.startDate.format('YYYY-MM-DD'),
            dateEnd: data.dateEnd.format('YYYY-MM-DD'),
            textResponseRequired: data.textResponseRequired,
            surveyTexts: surveyTexts,
        }

        try {
            const result = yield call(api.addNewSurveyByCountry, currentCountry, newSurvey)
            yield put(actions.setAddSurveySendingResult(result))
        } catch (e) {
            yield put(actions.setAddSurveySendingError(e.message))
        }
    })
}

export function* cancelSurveySaga() {
    yield takeLatest(constants.CANCEL_SURVEY, function* ({ id }) {
        try {
            yield indicateProgress([constants.CANCEL_SURVEY], function* () {
                const currentCountry = countryFromHost()
                const updatedSurvey = yield call(api.cancelSurveyById, currentCountry, id)
                yield put(actions.updateExistingSurvey(updatedSurvey))
            })
        } catch (e) {}
    })
}

export function* getSurveyCSVSaga() {
    yield takeLatest(constants.GET_SURVEY_CSV, function* ({ id }) {
        const currentCountry = countryFromHost()
        const actualSurvey = yield select(getActualSurvey)
        const csv = yield call(api.getSurveyCSV, currentCountry, id || actualSurvey.surveyId)
        yield put(actions.setCSVData(csv))
    })
}

export default function* rootSaga() {
    yield fork(getSurveyResultsByIdSaga)
    yield fork(getSurveyTextAnswerByIdSaga)
    yield fork(getSurveyCSVSaga)
    yield fork(npsAdminLoginSaga)
    yield fork(getAllUsersSurveysSaga)
    yield fork(sendSurveyAnswerSaga)
    yield fork(addNewSurveyByCountrySaga)
    yield fork(cancelSurveySaga)
    yield takeLatest(constants.GET_ALL_SURVEY_BY_COUNTRY_ADMIN, getAllSurveysAdminSaga)
}
